// pause-resume-state.ts
//
// Pause/resume semantics at the STATE level (spec §3.3 Req 5, §4 PAUSED row,
// tests 30-31). The real "stop accepting new dispatch" enforcement lives in
// supervisor.ts's dispatch loop (P6 scope) — this module owns the durable
// PAUSED/resume_context record that the dispatch loop (once built) reads, and
// is fully testable now: pause request => canAcceptNewDispatch() flips to
// false immediately (even though "after current attempt" timing is a runtime
// concern for P6); resume => wait conditions (gate/quota) captured at pause
// time are preserved verbatim, never cleared.

import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export interface PauseResumeState {
  paused: boolean;
  pause_requested_at?: string;
  resume_context: {
    wait_reasons: string[];
    gate_request_ids: string[];
    wake_at: string | null;
  } | null;
}

function statePath(controlRoot: string): string {
  return join(controlRoot, "pause-state.json");
}

export async function readPauseResumeState(controlRoot: string): Promise<PauseResumeState> {
  const path = statePath(controlRoot);
  if (!(await pathExists(path))) return { paused: false, resume_context: null };
  return readJson<PauseResumeState>(path);
}

async function writeState(controlRoot: string, state: PauseResumeState): Promise<void> {
  await writeFileDurable(
    statePath(controlRoot),
    Buffer.from(`${JSON.stringify(state, null, 2)}\n`, "utf8"),
  );
}

/**
 * Request pause: the dispatch loop must stop picking up NEW work after the
 * current attempt finishes (spec: "Pause dừng nhận việc mới sau attempt hiện
 * tại"). Captures current wait conditions (gate/quota) into resume_context so
 * resume can restore them exactly.
 */
export async function requestPause(
  controlRoot: string,
  currentWaitReasons: string[],
  currentGateRequestIds: string[],
  wakeAt: string | null,
  now: () => Date = () => new Date(),
): Promise<PauseResumeState> {
  const state: PauseResumeState = {
    paused: true,
    pause_requested_at: now().toISOString(),
    resume_context: {
      wait_reasons: currentWaitReasons,
      gate_request_ids: currentGateRequestIds,
      wake_at: wakeAt,
    },
  };
  await writeState(controlRoot, state);
  return state;
}

/**
 * Resume: paused flips to false, but resume_context (gate/quota wait
 * conditions) is PRESERVED, not cleared — the caller (dispatch loop) is
 * responsible for re-evaluating those conditions rather than treating resume
 * as "start fresh" (spec: "resume giữ phase và các điều kiện gate/quota còn
 * hiệu lực").
 */
export async function resumeFromPause(controlRoot: string): Promise<PauseResumeState> {
  const current = await readPauseResumeState(controlRoot);
  const next: PauseResumeState = { ...current, paused: false };
  await writeState(controlRoot, next);
  return next;
}

/** Whether the dispatch loop may pick up NEW work right now. */
export function canAcceptNewDispatch(state: PauseResumeState): boolean {
  return !state.paused;
}
